import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import moment from 'moment';
import {
    FaHourglassStart, 
    FaUserGraduate
} from 'react-icons/fa';
import { MdQuiz } from "react-icons/md";
import CoursePlaceholderImage from 'assets/vectors/CoursePlaceholder.svg';
import axios from 'config/axios';
import Helmet from 'components/Helmet';
import Button from 'components/Button';
import ButtonLink from 'components/ButtonLink';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { updateCart } from 'store/cart';
import { setAlert } from 'store/error';
import { Course, Bundle } from 'global/types';
import './index.css';

type NewCoursesProps = {
    courses: Course[];
    bundles: Bundle[];
};

function NewCourses({ courses, bundles }: NewCoursesProps) {
    const { user } = useAppSelector(state => state.user);
    const {
        bundles: cartBundles,
        courses: cartCourses,
        isUpdatingCart
    } = useAppSelector(state => state.cart);
    const { courses: classroomCourses } = useAppSelector(state => state.classroom);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const refreshCart = () => {
        axios.get('payments/cart/')
            .then(res => {
                // console.log(res.data);
                dispatch(updateCart({
                    bundles: res.data?.cartBundles,
                    courses: res.data?.cartCourses
                }));
            });
    }

    const addBundleToCart = (bundleId: string) => {
        if (user) {
            axios.post(`payments/cart/add/bundle/${bundleId}/`)
                .then(res => {
                    // console.log(res.data);
                    refreshCart();

                    if (res.data.price > 0) {
                        ReactGA.event('add_to_cart', {
                            currency: 'USD',
                            value: res.data.price,
                            items: [{
                                item_id: bundleId,
                                item_name: res.data.name
                            }]
                        });
                    }                    
                })
                .catch(error => {
                    // console.log(error);
                    dispatch(setAlert(error));
                });
        }
        else {
            navigate('/sign-in', {
                state: { referrer: location.pathname }
            });
        }
    }

    const addCourseToCart = (courseUrl: string) => {
        if (user) {
            axios.post(`payments/cart/add/course/${courseUrl}/`)
                .then(res => {
                    // console.log(res.data);
                    refreshCart();

                    if (res.data.tuition > 0) {
                        ReactGA.event('add_to_cart', {
                            currency: 'USD',
                            value: res.data.tuition,
                            items: [{
                                item_id: courseUrl,
                                item_name: res.data.name
                            }]
                        });
                    }
                })
                .catch(error => {
                    // console.log(error);
                    dispatch(setAlert(error));
                });
        }
        else {
            navigate('/sign-in', {
                state: { referrer: location.pathname }
            });
        }
    }

    const powerSet = (array: any[]) => {
        return new Array(1 << array.length).fill(null).map((_e1, i) => (
            array.filter((_e2, j) => i & 1 << j)
        )).sort((a, b) => b.length - a.length);
    }

    if (user && isUpdatingCart) return <></>;

    return (
        <>
            <Helmet
                title='Available Courses - The Digital SAT Crash Course'
                description='Purchase individual courses or buy in bundles based on the results from the free detailed report card. Get discounts when purchasing in bundles.'
            />
            {/* <h1 className='newCourses__header'>
                <BiRadar className='newCourses__headerIcon' />
                The SAT Crash Course
            </h1> */}
            <div className='newCourses__content'>
                <div className='newCourses__courses'>
                    {courses.map(course => (
                        <div className='newCourses__course' key={course.name}>
                            <img
                                className='newCourses__courseImage'
                                src={course.image
                                    ? course.image
                                    : CoursePlaceholderImage
                                }
                                alt={course.name}
                            />
                            <div className='newCourses__courseContent'>
                                <div className='newCourses__courseColumn'>
                                    <h3 className='newCourses__courseTitle'>
                                        {course.name}
                                    </h3>
                                    <div className='newCourses__courseInfo'>
                                        {course.teacher &&
                                            <p className='newCourses__courseInfoItem'>
                                                <FaUserGraduate className='newCourses__courseInfoIcon' />
                                                Teacher {course.teacher.firstName} {course.teacher.lastName}
                                            </p>
                                        }
                                        {course.description &&
                                            <p className='newCourses__courseInfoItem'>
                                                <MdQuiz className='newCourses__courseInfoIcon' />
                                                {course.description}
                                            </p>
                                        }
                                        {course.period &&
                                            <p className='newCourses__courseInfoItem'>
                                                <FaHourglassStart className='newCourses__courseInfoIcon' />
                                                Available for {Math.round(moment.duration(course.period).asMonths())} Months
                                            </p>
                                        }
                                    </div>
                                    {course.discount
                                        ?   <p className='newCourses__coursePrice--discount'>
                                                <span>${course.tuition.toFixed(2)}</span>
                                                ${course.discount?.type === 'Percent'
                                                    ? (course.tuition - (course.discount.value * course.tuition)/100).toFixed(2)
                                                    : course.discount?.type === 'Amount'
                                                    ? (course.tuition - course.discount.value).toFixed(2)
                                                    : course.tuition.toFixed(2)
                                                }
                                            </p>
                                        :   <p className='newCourses__coursePrice'>
                                                ${course.tuition.toFixed(2)}
                                            </p>
                                    }
                                </div>
                                <div className='newCourses__courseColumn'>
                                    <ButtonLink
                                        className='newCourses__courseButton'
                                        href={`https://thesatcrashcourse.com/${course.courseDetailUrl}`}
                                        secondary
                                    >
                                        LEARN MORE
                                    </ButtonLink>
                                    {classroomCourses.some(classroomCourse => classroomCourse.urlSlug === course.urlSlug)
                                        ?   <Button
                                                className='newCourses__courseButton'
                                                disabled
                                            >
                                                REGISTERED
                                            </Button>
                                        :   cartCourses.some(cartCourse => cartCourse.urlSlug === course.urlSlug)
                                            || cartBundles.some(cartBundle => cartBundle.courses.some(bundleCourse => bundleCourse.urlSlug === course.urlSlug))
                                        ?   <Button 
                                                className='newCourses__courseButton'
                                                disabled
                                            >
                                                CART ADDED
                                            </Button>
                                        :   <Button 
                                                className='newCourses__courseButton'
                                                onClick={() => addCourseToCart(course.urlSlug)}
                                            >
                                                ADD TO CART
                                            </Button>
                                    }
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className='newCourses__bundles'>
                    {bundles.filter(bundle => (
                        !bundle.courses.some(course => (
                            classroomCourses.some(classroomCourse => (
                                classroomCourse.urlSlug === course.urlSlug
                            ))
                        )) && (
                            cartBundles.filter(bundle => bundle.tests.length === 0).some(cartBundle => (
                                cartBundle.courses.every(cartBundleCourse => (
                                    bundle.courses.some(course => (
                                        cartBundleCourse.urlSlug === course.urlSlug
                                    ))
                                )) && bundle.courses.length >= cartBundle.courses.length
                            )) || bundle.courses.every(course => (
                                cartBundles.filter(bundle => bundle.tests.length === 0).every(cartBundle => (
                                    cartBundle.courses.every(cartBundleCourse => (
                                        course.urlSlug !== cartBundleCourse.urlSlug
                                    ))
                                ))
                            ))
                        ) && (
                            !powerSet(cartCourses).some(combination => (
                                bundles.filter(anotherBundle => anotherBundle !== bundle).some(anotherBundle => (
                                    bundle.courses.every(course => (
                                        anotherBundle.courses.some(bundleCourse => (
                                            course.urlSlug === bundleCourse.urlSlug
                                        ))
                                    )) && anotherBundle.courses.every(bundleCourse => (
                                        [...bundle.courses, ...combination].some(course => (
                                            course.urlSlug === bundleCourse.urlSlug
                                        ))
                                    ))
                                ))
                            ))
                        )
                    )).map(bundle => (
                        <div className='newCourses__bundle' key={bundle.name}>
                            <div className='newCourses__bundleInfo'>
                                <h2 className='newCourses__bundleHeader'>
                                    {bundle.name}
                                </h2>
                                <table className='newCourses__bundleTable'>
                                    <tbody>
                                        {bundle.courses.map(course => (
                                            <tr key={course.name}>
                                                <th>{course.name}</th>
                                                {course.period
                                                    ? <td>For {Math.round(moment.duration(course.period).asMonths())} Months</td>
                                                    : <td>${course.tuition.toFixed(2)}</td>
                                                }
                                            </tr>
                                        ))}
                                        <tr>
                                            <td className='newCourses__bundleTableDivider'></td>
                                            <td className='newCourses__bundleTableDivider'></td>
                                        </tr>
                                        <tr>
                                            <th>Total course fee</th>
                                            <td>
                                                {bundle.discount?.type === 'Percent'
                                                ?   <p className='newCourses__bundleDiscount'>
                                                        <span>${bundle.courses.reduce((total, { tuition }) => total + tuition, 0).toFixed(2)}</span>
                                                        ${(bundle.courses.reduce((total, { tuition }) => total + tuition, 0)*(100 - bundle.discount.value)/100).toFixed(2)}
                                                    </p>
                                                :   bundle.discount?.type === 'Amount'
                                                ?   <p className='newCourses__bundleDiscount'>
                                                        <span>${bundle.courses.reduce((total, course) => total + course.tuition, 0).toFixed(2)}</span>
                                                        ${(bundle.courses.reduce((total, course) => total + course.tuition, 0) - bundle.discount.value).toFixed(2)}
                                                    </p>
                                                :   <p>${bundle.courses.reduce((total, { tuition }) => total + tuition, 0).toFixed(2)}</p>
                                                }
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            {bundle.courses.every(course => (
                                    cartBundles.some(cartBundle => (
                                        cartBundle.courses.some(cartBundleCourse => (
                                            cartBundleCourse.urlSlug === course.urlSlug
                                        ))
                                    ))
                                ))
                                ?   <Button
                                        className='newCourses__bundleButton'
                                        disabled
                                    >
                                        CART ADDED
                                    </Button>
                                :   <Button
                                        className='newCourses__bundleButton'
                                        onClick={() => addBundleToCart(bundle.id)}
                                    >
                                        ADD TO CART
                                    </Button>
                            }
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}

export default NewCourses;